import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Subject, catchError, concatMap, finalize, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../authentication/services/auth.service';
import { CurseursBibata, genererCurseursBibata } from '../utils/bibata-curseurs';

export type AppCursorId = 'system' | 'bibata';

interface CursorVariants {
  light: string;
  /** Variante pour le mode sombre (classe `dark` sur <html>). */
  dark: string;
}

interface CursorOption {
  id: AppCursorId;
  label: string;
  description: string;
  image: string | null;
  cursor: CursorVariants;
  /** Liens et boutons, puis zones de saisie : sans variante, la flèche partout. */
  pointer?: CursorVariants;
  text?: CursorVariants;
}

interface CursorSave {
  id: AppCursorId;
  userId: number;
}

/**
 * Images rendues à chaque densité courante (Windows 125 % et 150 % compris) :
 * le navigateur n'a jamais à agrandir ni réduire le curseur. Chromium sous
 * Windows assombrit les pixels clairs semi-transparents d'un curseur : un jeu
 * d'images par mode, chacun lisse sur ses propres fonds (voir
 * scripts/generer-curseurs.py). Après régénération, incrémenter `v`.
 */
function imageCursor(image: string, hotspot: string, repli: string): string {
  const v = '?v=8';
  const densities = [['', '1x'], ['@1.25x', '1.25x'], ['@1.5x', '1.5x'], ['@2x', '2x'], ['@3x', '3x']];
  const images = `image-set(${densities.map(([suffix, x]) => `url("assets/cursors/${image}${suffix}.png${v}") ${x}`).join(', ')})`;
  const value = `${images} ${hotspot}, ${repli}`;
  return CSS.supports('cursor', value) ? value : repli;
}

function variants(image: string, hotspot: string, repli: string): CursorVariants {
  return { light: imageCursor(image, hotspot, repli), dark: imageCursor(`${image}-sombre`, hotspot, repli) };
}

/** Couleur du curseur Bibata : noir (images fixes), couleur du thème, ou « R, G, B ». */
export type CouleurCurseur = 'noir' | 'theme' | string;

function normaliserCouleur(valeur: unknown): CouleurCurseur {
  if (valeur === 'noir' || valeur === 'theme') return valeur;
  return typeof valeur === 'string' && /^\d{1,3}, ?\d{1,3}, ?\d{1,3}$/.test(valeur.trim()) ? valeur.trim() : 'noir';
}

const CURSOR_PROPERTIES = [
  '--app-cursor', '--app-cursor-dark', '--app-cursor-pointer', '--app-cursor-pointer-dark', '--app-cursor-text', '--app-cursor-text-dark',
];

/** Curseurs natifs du navigateur : aucun calque ni écouteur de mouvement. */
@Injectable({ providedIn: 'root' })
export class CursorService {
  private readonly auth = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly toastr = inject(ToastrService);
  private readonly configKey = 'app_cursor';
  private readonly selected = signal<AppCursorId>('system');
  private readonly saves = new Subject<CursorSave>();
  private pending: CursorSave | null = null;
  private savedCursor: AppCursorId = 'system';

  readonly current = this.selected.asReadonly();

  private readonly colorKey = 'app_cursor_color';
  private readonly couleurChoisie = signal<CouleurCurseur>('noir');
  readonly couleur = this.couleurChoisie.asReadonly();
  readonly couleursProposees: ReadonlyArray<{ id: CouleurCurseur; label: string }> = [
    { id: 'noir', label: 'Noir' },
    { id: 'theme', label: 'Couleur du thème' },
    { id: '8, 59, 94', label: 'Bleu nuit' },
    { id: '181, 40, 36', label: 'Rouge' },
    { id: '109, 40, 217', label: 'Violet' },
    { id: '4, 120, 87', label: 'Vert' },
  ];
  private readonly sauvegardesCouleur = new Subject<CouleurCurseur>();
  private couleurEnVol: CouleurCurseur | null = null;
  /** Rendus déjà faits, par couleur « R, G, B » (aller-retour clair/sombre, changement de thème). */
  private readonly rendus = new Map<string, Promise<CurseursBibata | null>>();
  private rendu = 0;
  private observateurTheme: MutationObserver | null = null;
  private couleurTheme = '';
  readonly options: readonly CursorOption[] = [
    { id: 'system', label: 'Système', description: 'Le curseur habituel du thème', image: null, cursor: { light: 'auto', dark: 'auto' } },
    {
      // Bibata Modern Classic, ful1e5 — GPL-3.0 (assets/cursors/bibata/).
      id: 'bibata', label: 'Bibata', description: 'Flèche, main et barre de texte arrondies',
      image: 'assets/cursors/bibata-fleche@3x.png', cursor: variants('bibata-fleche', '5 3', 'auto'),
      pointer: variants('bibata-main', '11 2', 'pointer'),
      text: variants('bibata-texte', '12 12', 'text'),
    },
  ];

  constructor() {
    // Le serveur peut corriger le cache de session. Seules les publications
    // pendant une sauvegarde sont ignorées pour ne pas annuler un choix en cours.
    this.auth.user.pipe(
      takeUntilDestroyed(),
    ).subscribe(user => {
      if (!this.couleurEnVol) this.couleurChoisie.set(normaliserCouleur(user?.config?.[this.colorKey]));
      if (this.pending && this.pending.userId === user?.id) return;
      this.pending = null;
      this.savedCursor = this.normalize(user?.config?.[this.configKey]);
      this.apply(this.savedCursor);
    });

    // Sérialiser les clics rapides pour que le dernier choix reste le dernier écrit.
    this.saves.pipe(
      concatMap(save => {
        const { id, userId } = save;
        if (this.auth.user.value?.id !== userId) return EMPTY;
        return this.http.patch('api/users/config/', { [this.configKey]: id }, { withCredentials: true }).pipe(
          tap(() => {
            if (this.auth.user.value?.id === userId) this.savedCursor = id;
          }),
          catchError(() => {
            if (this.auth.user.value?.id === userId) {
              this.toastr.warning('Le choix n’a pas pu être enregistré. Le dernier curseur sauvegardé sera rétabli.', 'Préférence non enregistrée');
            }
            return EMPTY;
          }),
          finalize(() => {
            if (this.pending !== save || this.auth.user.value?.id !== userId) return;
            this.pending = null;
            this.apply(this.savedCursor);
            this.publishPreference(this.savedCursor);
          }),
        );
      }),
      takeUntilDestroyed(),
    ).subscribe();

    this.sauvegardesCouleur.pipe(
      concatMap(couleur => this.http.patch('api/users/config/', { [this.colorKey]: couleur }, { withCredentials: true }).pipe(
        catchError(() => {
          this.toastr.warning('La couleur du curseur n’a pas pu être enregistrée.', 'Préférence non enregistrée');
          return EMPTY;
        }),
        finalize(() => {
          if (this.couleurEnVol === couleur) this.couleurEnVol = null;
        }),
      )),
      takeUntilDestroyed(),
    ).subscribe();
  }

  /** Couleur du curseur Bibata ; l'état local et le cache de session passent avant le serveur. */
  setCouleur(valeur: CouleurCurseur): void {
    const couleur = normaliserCouleur(valeur);
    if (couleur === this.couleurChoisie()) return;
    this.couleurChoisie.set(couleur);
    this.apply(this.selected());
    const user = this.auth.user.value;
    if (!user) return;
    this.couleurEnVol = couleur;
    const updatedUser = { ...user, config: { ...user.config, [this.colorKey]: couleur } };
    this.auth.user.next(updatedUser);
    this.auth.cacheUser(updatedUser);
    this.sauvegardesCouleur.next(couleur);
  }

  setCursor(id: AppCursorId): void {
    const next = this.normalize(id);
    this.apply(next);
    const user = this.auth.user.value;
    if (!user) return;

    const save = { id: next, userId: user.id };
    this.pending = save;
    this.publishPreference(next);
    this.saves.next(save);
  }

  private publishPreference(id: AppCursorId): void {
    const user = this.auth.user.value;
    if (!user) return;
    const updatedUser = { ...user, config: { ...user.config, [this.configKey]: id } };
    this.auth.user.next(updatedUser);
    this.auth.cacheUser(updatedUser);
  }

  private normalize(value: unknown): AppCursorId {
    // Graphite, retiré : ses utilisateurs passent sur Bibata (migration users 0034 côté serveur).
    if (value === 'graphite') return 'bibata';
    return this.options.find(option => option.id === value)?.id ?? 'system';
  }

  private apply(id: AppCursorId): void {
    this.selected.set(id);
    const root = this.document.documentElement;
    const option = this.options.find(item => item.id === id);
    this.rendu++;
    const colore = id === 'bibata' && this.couleurChoisie() !== 'noir';
    this.suivreCouleurTheme(colore && this.couleurChoisie() === 'theme');
    if (id === 'system' || !option) {
      root.removeAttribute('data-app-cursor');
      for (const nom of CURSOR_PROPERTIES) root.style.removeProperty(nom);
      return;
    }
    // Les images fixes d'abord : la version colorée les remplace dès qu'elle est rendue.
    this.poser(option.cursor, option.pointer ?? option.cursor, option.text ?? option.cursor);
    root.setAttribute('data-app-cursor', id);
    if (colore) this.colorer();
  }

  private poser(cursor: CursorVariants, pointer: CursorVariants, text: CursorVariants): void {
    const root = this.document.documentElement;
    root.style.setProperty('--app-cursor', cursor.light);
    root.style.setProperty('--app-cursor-dark', cursor.dark);
    root.style.setProperty('--app-cursor-pointer', pointer.light);
    root.style.setProperty('--app-cursor-pointer-dark', pointer.dark);
    root.style.setProperty('--app-cursor-text', text.light);
    root.style.setProperty('--app-cursor-text-dark', text.dark);
  }

  /** Bibata dans la couleur choisie, rendu dans le navigateur (repli : les images noires restent). */
  private colorer(): void {
    const couleur = this.couleurChoisie();
    const rgb = couleur === 'theme' ? this.lireCouleurTheme() : couleur;
    if (!rgb) return;
    const jeton = this.rendu;
    let rendu = this.rendus.get(rgb);
    if (!rendu) {
      rendu = genererCurseursBibata(rgb);
      this.rendus.set(rgb, rendu);
    }
    void rendu.then(curseurs => {
      if (curseurs && jeton === this.rendu) this.poser(curseurs.fleche, curseurs.main, curseurs.texte);
    });
  }

  private lireCouleurTheme(): string {
    const root = this.document.documentElement;
    return (root.style.getPropertyValue('--main-color-rgb') || getComputedStyle(root).getPropertyValue('--main-color-rgb')).trim();
  }

  /** « Couleur du thème » : la couleur principale change avec le thème, le mode et les saisons. */
  private suivreCouleurTheme(actif: boolean): void {
    if (!actif) {
      this.observateurTheme?.disconnect();
      this.observateurTheme = null;
      return;
    }
    this.couleurTheme = this.lireCouleurTheme();
    if (this.observateurTheme) return;
    this.observateurTheme = new MutationObserver(() => {
      const couleur = this.lireCouleurTheme();
      if (couleur === this.couleurTheme) return;
      this.couleurTheme = couleur;
      this.rendu++;
      this.colorer();
    });
    this.observateurTheme.observe(this.document.documentElement, { attributes: true, attributeFilter: ['style'] });
  }
}
